import { useEffect, useState } from 'react';
import type { ComponentHealth } from '../../services/api';
import { api } from '../../services/api';
import { cx } from '../../utils/cx';
import styles from './SystemStatus.module.css';

// Невідомі ключі `/health` не показуємо: назви українською для них немає.
const COMPONENTS: ReadonlyArray<readonly [key: string, label: string]> = [
  ['postgres', 'База даних'],
  ['qdrant', 'Пошуковий індекс'],
  ['minio', 'Сховище файлів'],
  ['redis', 'Обробка документів'],
  ['embeddings', 'Пошук за змістом'],
  ['llm', 'Мовна модель'],
];

type View =
  | { kind: 'loading' }
  | { kind: 'unknown' }
  | { kind: 'ready'; info: Record<string, ComponentHealth> };

// Якщо `/health` не відповів — тихий рядок, а не банер помилки.
export function SystemStatus() {
  const [view, setView] = useState<View>({ kind: 'loading' });

  useEffect(() => {
    // api.health не приймає AbortSignal, тому прапорець.
    let alive = true;

    api
      .health()
      .then((report) => {
        if (!alive) return;
        setView(
          hasComponents(report.info) ? { kind: 'ready', info: report.info } : { kind: 'unknown' },
        );
      })
      .catch(() => {
        if (!alive) return;
        setView({ kind: 'unknown' });
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className={styles.card}>
      <h2 className={styles.title}>Стан системи</h2>

      {view.kind === 'ready' ? (
        COMPONENTS.map(([key, label]) => {
          const component = view.info[key];
          if (!component) return null;
          return (
            <p className={styles.row} key={key}>
              <span className={dotClasses(component.status)} />
              {label}
              <span className={styles.sr}>{statusWord(component.status)}</span>
            </p>
          );
        })
      ) : (
        <p className={styles.row}>
          <span className={styles.dot} />
          {view.kind === 'loading' ? 'Перевіряємо стан' : 'Стан невідомий'}
        </p>
      )}
    </section>
  );
}

function dotClasses(status: ComponentHealth['status']): string {
  return cx(styles.dot, statusClass(status));
}

function statusClass(status: ComponentHealth['status']): string | undefined {
  if (status === 'up') return styles.ok;
  // `skipped` — не помилка, але й не «все добре».
  if (status === 'skipped') return styles.warn;
  return styles.bad;
}

function statusWord(status: ComponentHealth['status']): string {
  if (status === 'up') return 'працює';
  if (status === 'skipped') return 'не використовується';
  return 'не працює';
}

// `info` може приїхати не тієї форми — падало б уже в рендері.
function hasComponents(info: unknown): info is Record<string, ComponentHealth> {
  return typeof info === 'object' && info !== null;
}
